import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useAppStore } from '../../store/appStore';

interface AuthGuardProps {
  children: ReactNode;
  requiredRoles?: string[];
  requireAll?: boolean; // If true, user must have ALL roles. If false, user must have ANY role.
}

/**
 * AuthGuard - Protects routes that require authentication.
 *
 * Offline behaviour:
 *  - If the user is authenticated (persisted state), they are allowed through
 *    regardless of connectivity.
 *  - If the user is NOT authenticated and the app is offline, we cannot
 *    redirect to the SAML IdP (an internet endpoint). Instead we render an
 *    inline message so the SPA remains on-screen and the browser does not
 *    show the Chromium ERR_INTERNET_DISCONNECTED page.
 *
 * Usage:
 * ```tsx
 * <Route path="/admin" element={<AuthGuard requiredRoles={['Admin']}><AdminPage /></AuthGuard>} />
 * ```
 */
export function AuthGuard({ children, requiredRoles, requireAll = false }: AuthGuardProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, isLoading, hasRole, hasAllRoles, login } = useAuthStore();
  const { isOffline } = useAppStore();

  // Wait for auth state to load (persisted store hydrates synchronously,
  // but loadIdentity sets isLoading: true briefly on first online boot).
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh' 
      }}>
        <p>{t('pages.loading')}</p>
      </div>
    );
  }

  // If not authenticated, check connectivity before redirecting.
  if (!isAuthenticated) {
    if (isOffline) {
      // Cannot reach the SAML IdP while offline — show a message instead
      // of navigating to an unreachable URL.
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <h2>{t('auth.youAreOffline')}</h2>
          <p>{t('auth.offlineReconnectMessage')}</p>
        </div>
      );
    }

    // Online and not authenticated — initiate SAML login.
    login(location.pathname);
    return null; // The login() call will redirect, so we don't render anything
  }

  // If roles are required, check authorization
  if (requiredRoles && requiredRoles.length > 0) {
    const hasPermission = requireAll 
      ? hasAllRoles(requiredRoles)
      : hasRole(requiredRoles);

    if (!hasPermission) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <h2>{t('auth.accessDenied')}</h2>
          <p>{t('auth.noPermissionMessage')}</p>
          <button onClick={() => window.location.href = '/'}>
            {t('auth.returnHome')}
          </button>
        </div>
      );
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
}
